/**
 * AddCourse Component
 * 
 * Purpose: Handles both adding new courses and editing existing courses
 * Location: /admin/course/add (Add) or /admin/course/edit/:id (Edit)
 * 
 * Features:
 * - Dynamic form handling for course creation/editing
 * - Multi-school selection with "Select All" functionality
 * - Grade assignment with "All Grades" option (filtered by selected school)
 * - Rich text description using CKEditor
 * - Image upload with preview
 * - Role-based UI (Admin vs School user views)
 * 
 * User Types:
 * - Admin: Can select multiple schools, sees school dropdown
 * - School Admin: Course auto-assigned to their school, no school dropdown
 */

import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Form, Row, Col, Button, Card, Spinner } from "react-bootstrap";
import { UserContext } from "../../context/UserContextProvider";
import APIServicenew from "../../utils/APIGeneralService";
import { ToastContainer, toast } from "react-toastify";
import Select, { components } from "react-select";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Add state for date
/**
 * Main AddCourse Component
 * Manages course creation and editing based on URL parameter (id)
 */
const AddCourse = () => {
  // Navigation and routing hooks
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const today = new Date().toISOString().split("T")[0];
  const [expiryDate, setExpiryDate] = useState(null);

  // Character limit constants
  const CHAR_LIMITS = {
    title: 100,
    sub_title: 200,
    duration: 30,
    language: 20
  };

  // Authentication credentials from environment and context
  const authToken = process.env.REACT_APP_authToken;
  const { accessToken, logout, data } = useContext(UserContext);

  // API configuration headers
  const headers = {
    "auth-token": authToken,
    "authorization": accessToken
  };

  // Initialize API service with course API URL
  const APIServices = new APIServicenew(process.env.REACT_APP_OTHER_API_URL);

  // School selection state (for admin users)
  const [preselectedSchool, setPreselectedSchool] = useState(null);
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(false);

  // Pre-selected school from URL query params (used during course creation from school context)
  const [preselectedSchoolFromUrl, setPreselectedSchoolFromUrl] = useState(null);
  const [preselectedSchoolName, setPreselectedSchoolName] = useState("");
  const [schoolId, setSchoolId] = useState(null);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [loadingGrades, setLoadingGrades] = useState(false);

  // Grades data from API
  const [grades, setGrades] = useState([]);

  // Course form data
  const [formData, setFormData] = useState({
    title: "",
    sub_title: "",
    duration: "",
    grade_id: [],
    description: "",
    language: "",
    expiry_date: "",
    image: null
  });

  // Form validation errors
  const [errors, setErrors] = useState({});

  // Image handling state
  const [existingImage, setExistingImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  /**
   * Extract school_id from URL query parameters
   * Used when course is being created from a school context
   */
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const schoolIdFromUrl = queryParams.get('school_id');

    if (schoolIdFromUrl) {
      setPreselectedSchoolFromUrl(schoolIdFromUrl);
    }
  }, [location]);

  // Fetch schools when component mounts (for add mode)
  useEffect(() => {
    if (!id) {
      fetchSchools();
    }
  }, [preselectedSchoolFromUrl]);

  /**
   * Custom Option component for react-select
   * Adds checkbox functionality to each school option
   */
  const Option = (props) => {
    return (
      <div>
        <components.Option {...props}>
          <input
            type="checkbox"
            checked={props.isSelected}
            onChange={() => null}
            style={{ marginRight: '8px' }}
          />
          <label>{props.label}</label>
        </components.Option>
      </div>
    );
  };

  /**
   * Custom MenuList component for react-select
   * Adds "Select All Schools" option at the top of dropdown
   */
  const MenuList = (props) => {
    const { children, options, getValue, selectProps } = props;
    const selectedValues = getValue();
    const isAllSelected = selectedValues.length === options.length && options.length > 0;

    const handleSelectAll = () => {
      if (isAllSelected) {
        selectProps.onChange([], { action: 'clear' });
      } else {
        selectProps.onChange(options, { action: 'select-all' });
      }
    };

    return (
      <div>
        <div
          style={{
            padding: '8px 12px',
            borderBottom: '1px solid #ccc',
            cursor: 'pointer',
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold'
          }}
          onClick={handleSelectAll}
        >
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={() => { }}
            style={{ marginRight: '8px' }}
          />
          Select All Schools
        </div>
        <components.MenuList {...props}>
          {children}
        </components.MenuList>
      </div>
    );
  };

  // Fetch grades when school selection changes
  useEffect(() => {
    if (!id) {
      fetchGrades();
    }
  }, [selectedSchools, preselectedSchoolFromUrl, schoolId]);

  // If editing (id exists), fetch course details
  useEffect(() => {
    if (id) {
      fetchCourseDetails();
    }
  }, [id]);

  /**
   * Fetch existing course details for edit mode
   * Populates form with current course data
   */
 const fetchCourseDetails = async () => {
  setFetchingDetails(true);
  try {
    const response = await APIServices.get(
      `/admin/courses/${id}`,
      headers
    );

    if (response?.data?.status === true) {
      const courseData = response?.data?.data;

      // Parse grade_id from comma-separated string or "all"
      let gradeIdArray = [];
      if (courseData.grade_id) {
        if (courseData.grade_id === "all") {
          gradeIdArray = ["all"];
        } else {
          gradeIdArray = courseData.grade_id.split(',').map(id => id.trim());
        }
      }

      // Convert expiry date from YYYY-MM-DD to Date object
      let expiryDateObj = null;
      if (courseData.expiry_date) {
        const expiryDateStr = courseData.expiry_date.split(' ')[0];
        expiryDateObj = new Date(expiryDateStr);
      }

      setFormData({
        title: courseData.title || "",
        sub_title: courseData.sub_title || "",
        duration: courseData.duration || "",
        grade_id: gradeIdArray,
        description: courseData.description || "",
        language: courseData.language || "",
        expiry_date: courseData.expiry_date ? courseData.expiry_date.split(' ')[0] : "",
        image: null
      });

      // Set the date picker state
      setExpiryDate(expiryDateObj);
      setExistingImage(courseData.image || null);
      
      // Fetch grades for edit mode
      await fetchGrades();
    }
  } catch (error) {
    console.error("Error fetching course details:", error);
    showToast("Error fetching course details", "error");
  } finally {
    setFetchingDetails(false);
  }
};

  /**
   * Fetch active grades from API
   * If school_id is present in URL params, send it
   * If multiple schools are selected, don't send school_id (get all grades)
   */
  const fetchGrades = async () => {
    setLoadingGrades(true);
    try {
      let url = `/admin/cms/grade_list?skip=0&limit=1000&status=Active`;

      // Check if we have a school_id from URL params
      if (preselectedSchoolFromUrl) {
        url = `/admin/cms/grade_list?skip=0&limit=1000&school_id=${preselectedSchoolFromUrl}`;
      }
      // Check if school admin user
      else if (data?.user_type === "school" && schoolId) {
        url = `/admin/cms/grade_list?skip=0&limit=1000&school_id=${schoolId}`;
      }
      // For admin users: if only ONE school is selected, send school_id
      else if (data?.user_type !== "school" && selectedSchools.length === 1) {
        const singleSchoolId = selectedSchools[0].value;
        url = `/admin/cms/grade_list?skip=0&limit=1000&school_id=${singleSchoolId}`;
      }
      // If multiple schools selected or no school selected, get all grades (no school_id filter)

      const response = await APIServices.get(url, headers);

      if (response?.data?.status === true) {
        const gradesData = response?.data?.data?.grades || response?.data?.data?.data || [];
        setGrades(gradesData);
      } else {
        setGrades([]);
      }
    } catch (error) {
      console.error("Error fetching grades:", error);
      showToast("Error fetching grades", "error");
      setGrades([]);
    } finally {
      setLoadingGrades(false);
    }
  };

  /**
   * Fetch schools from API
   * Priority handling:
   * 1. School from URL query params
   * 2. School admin's own school
   * 3. All schools for admin users
   */
  const fetchSchools = async () => {
    setLoadingSchools(true);
    try {
      const response = await APIServices.get(
        `/admin/school/short_list`,
        headers
      );

      if (response?.data?.status === true) {
        const schoolsData = response?.data?.data?.data || [];
        setSchools(schoolsData);

        // Priority 1: School ID from URL query params
        if (preselectedSchoolFromUrl) {
          const schoolFromUrl = schoolsData.find(school => school.id == preselectedSchoolFromUrl);
          if (schoolFromUrl) {
            setPreselectedSchoolName(schoolFromUrl.school_name);
            setSchoolId(schoolFromUrl.id);
          } else {
            setPreselectedSchoolName("School not found");
          }
        }
        // Priority 2: School admin user - auto-match to their school
        else if (data?.user_type === "school") {
          const userSchool = schoolsData.find(school => school.admin_id === data?.id);
          if (userSchool) {
            setSchoolId(userSchool.id);
            setPreselectedSchoolName(userSchool.school_name);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching schools:", error);
      showToast("Error fetching schools", "error");
    } finally {
      setLoadingSchools(false);
    }
  };

  /**
   * Handle school selection from react-select dropdown
   */
  const handleSchoolChange = (selectedOptions) => {
    setSelectedSchools(selectedOptions || []);

    // Clear grade selection when schools change
    setFormData(prev => ({ ...prev, grade_id: [] }));

    if (errors.school_ids) {
      setErrors({ ...errors, school_ids: "" });
    }
  };

  /**
   * Handle grade checkbox changes
   * Special handling for "All Grades" option - selecting "All" clears other selections
   */
  const handleGradeChange = (gradeId, isChecked) => {
    let updatedGrades = [...formData.grade_id];

    if (gradeId === "all") {
      // "All Grades" selected - clear other selections
      if (isChecked) {
        updatedGrades = ["all"];
      } else {
        updatedGrades = [];
      }
    } else {
      // Individual grade selection
      // Clear "all" if selecting individual grade
      if (updatedGrades.includes("all")) {
        updatedGrades = [];
      }

      if (isChecked) {
        updatedGrades.push(gradeId);
      } else {
        updatedGrades = updatedGrades.filter(id => id !== gradeId);
      }
    }

    setFormData({ ...formData, grade_id: updatedGrades });
    if (errors.grade_id) {
      setErrors({ ...errors, grade_id: "" });
    }
  };

  /**
   * Handle form input changes
   * Handles both regular inputs and file uploads with character limits
   */
  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === 'file') {
      // Handle image file upload with preview
      const file = files[0];
      setFormData({ ...formData, [name]: file });

      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setImagePreview(null);
      }
      setExistingImage(null);
    } else {
      // Apply character limits
      let limitedValue = value;
      if (CHAR_LIMITS[name]) {
        limitedValue = value.slice(0, CHAR_LIMITS[name]);
      }
      setFormData({ ...formData, [name]: limitedValue });
    }

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  /**
   * Form validation function
   * Returns object with validation errors
   */
  const validate = () => {
    const newErrors = {};

    if (!formData.title?.trim()) {
      newErrors.title = "Course title is required";
    } else if (formData.title.length > CHAR_LIMITS.title) {
      newErrors.title = `Course title cannot exceed ${CHAR_LIMITS.title} characters`;
    }

    if (!formData.sub_title?.trim()) {
      newErrors.sub_title = "Sub title is required";
    } else if (formData.sub_title.length > CHAR_LIMITS.sub_title) {
      newErrors.sub_title = `Sub title cannot exceed ${CHAR_LIMITS.sub_title} characters`;
    }

    if (!formData.duration?.trim()) {
      newErrors.duration = "Duration is required";
    } else if (formData.duration.length > CHAR_LIMITS.duration) {
      newErrors.duration = `Duration cannot exceed ${CHAR_LIMITS.duration} characters`;
    }

    if (formData.grade_id.length === 0) {
      newErrors.grade_id = "At least one grade is required";
    }
    if (!formData.description?.trim()) {
      newErrors.description = "Description is required";
    }
    if (!formData.language) {
      newErrors.language = "Language is required";
    } else if (formData.language.length > CHAR_LIMITS.language) {
      newErrors.language = `Language cannot exceed ${CHAR_LIMITS.language} characters`;
    }
    if (!formData.expiry_date) {
      newErrors.expiry_date = "Expiry date is required";
    }
    // Image required only for new courses
    if (!id && !formData.image) {
      newErrors.image = "Course image is required";
    }
    // Schools required only for admin users creating new courses
    if (!id && data?.user_type !== "school" && !preselectedSchoolFromUrl && selectedSchools.length === 0) {
      newErrors.school_ids = "At least one school is required";
    }

    return newErrors;
  };

  /**
   * Handle form submission
   * Creates new course or updates existing based on id presence
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showToast("Please fill all required fields", "error");
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();

      // Append course details
      formDataToSend.append("title", formData.title);
      formDataToSend.append("sub_title", formData.sub_title);
      formDataToSend.append("duration", formData.duration);

      // Process grade_ids - either "all" or comma-separated string
      let gradeIdsString;
      if (formData.grade_id.includes("all")) {
        gradeIdsString = "all";
      } else {
        gradeIdsString = formData.grade_id.join(',');
      }
      formDataToSend.append("grade_id", gradeIdsString);

      formDataToSend.append("description", formData.description);
      formDataToSend.append("language", formData.language);
      formDataToSend.append("expiry_date", formData.expiry_date);

      // Append image only if new file selected
      if (formData.image) {
        formDataToSend.append("image", formData.image);
      }

      // Append school_ids based on user type and context
      if (!id) {
        if (preselectedSchoolFromUrl) {
          // Course created from school context via URL
          formDataToSend.append("school_ids", preselectedSchoolFromUrl);
        } else if (data?.user_type === "school") {
          // School admin - auto-assign to their school
          if (schoolId) {
            formDataToSend.append("school_ids", schoolId.toString());
          }
        } else {
          // Admin user - use selected schools or "all"
          if (selectedSchools.length > 0) {
            if (selectedSchools.length === schoolOptions.length) {
              formDataToSend.append("school_ids", "all");
            } else {
              const schoolIds = selectedSchools.map(school => school.value).join(',');
              formDataToSend.append("school_ids", schoolIds);
            }
          }
        }
      }

      let response;

      // API call based on add vs edit mode
      if (id) {
        formDataToSend.append("id", id);
        response = await APIServices.put(
          "/admin/courses/update",
          formDataToSend,
          {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        );
      } else {
        response = await APIServices.post(
          "/admin/courses/add",
          formDataToSend,
          {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        );
      }

      // Handle response
      if (response?.data?.status === true) {
        showToast(response?.data?.message || (id ? "Course updated successfully" : "Course added successfully"), "success");
        setTimeout(() => {
          navigate("/admin/course");
        }, 2000);
      } else if (response?.status === 401) {
        // Unauthorized - logout and redirect
        logout();
        navigate("/");
      } else {
        showToast(response?.data?.message || "Something went wrong", "error");
      }
    } catch (error) {
      console.error("Error saving course:", error);
      showToast(error.response?.data?.message || "An unexpected error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Display toast notification
   * @param {string} message - Message to display
   * @param {string} type - Toast type (success/error/info)
   */
  const showToast = (message, type) => {
    toast(message, {
      position: "top-right",
      autoClose: 2000,
      type,
      hideProgressBar: false,
      closeOnClick: true,
    });
  };

  // Transform schools data for react-select component
  const schoolOptions = schools.map(school => ({
    value: school.id,
    label: `${school.school_name} (${school.school_code})`
  }));

  // Show loading spinner while fetching course details in edit mode
  if (fetchingDetails) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading course details...</p>
      </div>
    );
  }

  return (
    <>
      <div className="add_course_sec">
        {/* Page Header */}
        <div className="addmom_header d-flex justify-content-between align-items-center mb-3">
          <div>
            <h1 className="d-inline-block font_blue_18 font-600">
              {id ? "Edit Course" : "Add Course"}
            </h1>
          </div>
        </div>

        {/* Course Form Card */}
        <Card className="add_course_form">
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              {/* Schools Selection - Only for Admin Users (not school admin) and not pre-selected from URL */}
              {!id && data?.user_type !== "school" && !preselectedSchoolFromUrl && (
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Select Schools <span className="text-danger">*</span></Form.Label>
                      {loadingSchools ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <Select
                          options={schoolOptions}
                          isMulti
                          isSearchable={true}
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                          components={{ Option, MenuList }}
                          onChange={handleSchoolChange}
                          value={selectedSchools}
                          placeholder="Select schools..."
                          noOptionsMessage={({ inputValue }) =>
                            inputValue ? `No schools found matching "${inputValue}"` : "Type to search schools..."
                          }
                          className="react-select-container"
                          classNamePrefix="react-select"
                          styles={{
                            control: (base) => ({
                              ...base,
                              borderColor: errors.school_ids ? '#dc3545' : '#ced4da',
                              '&:hover': {
                                borderColor: errors.school_ids ? '#dc3545' : '#80bdff'
                              }
                            })
                          }}
                        />
                      )}
                      {errors.school_ids && (
                        <div className="text-danger mt-1" style={{ fontSize: '0.875rem' }}>
                          {errors.school_ids}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {/* Show pre-selected school from URL (read-only) */}
              {!id && preselectedSchoolFromUrl && (
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>School <span className="text-danger">*</span></Form.Label>
                      {loadingSchools ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <Form.Control
                          type="text"
                          value={preselectedSchoolName || "Loading school..."}
                          disabled
                          style={{ backgroundColor: '#e9ecef' }}
                        />
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {/* Course Title */}
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Course Title <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      placeholder="Enter course title"
                      value={formData.title}
                      onChange={handleChange}
                      isInvalid={!!errors.title}
                      disabled={loading}
                      maxLength={CHAR_LIMITS.title}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.title}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {/* Sub Title / Short Description */}
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Sub Title <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      rows={4}
                      name="sub_title"
                      placeholder="Enter sub title / short description"
                      value={formData.sub_title}
                      onChange={handleChange}
                      isInvalid={!!errors.sub_title}
                      disabled={loading}
                      maxLength={CHAR_LIMITS.sub_title}

                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.sub_title}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {/* Duration, Language, Expiry Date - Three columns */}
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Duration <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="duration"
                      placeholder="e.g., 2 months, 6 weeks, 1 year"
                      value={formData.duration}
                      onChange={handleChange}
                      isInvalid={!!errors.duration}
                      disabled={loading}
                      maxLength={CHAR_LIMITS.duration}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.duration}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Language <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="language"
                      placeholder="e.g., English, Hindi, etc."
                      value={formData.language}
                      onChange={handleChange}
                      isInvalid={!!errors.language}
                      disabled={loading}
                      maxLength={CHAR_LIMITS.language}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.language}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3 expiry-date-picker">
                    <Form.Label>Expiry Date <span className="text-danger">*</span></Form.Label>
                    <br></br>
                    <DatePicker
                      selected={expiryDate}
                      onChange={(date) => {
                        setExpiryDate(date);
                        const formattedDate = date ? date.toLocaleDateString('en-US') : '';
                        handleChange({ target: { name: 'expiry_date', value: formattedDate } });
                      }}
                      dateFormat="MM/dd/yyyy"
                      placeholderText="mm/dd/yyyy"
                      className="form-control w-100"
                      minDate={new Date()}
                      disabled={loading}
                      isInvalid={!!errors.expiry_date}
                    />
                    {errors.expiry_date && (
                      <div className="invalid-feedback d-block">
                        {errors.expiry_date}
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {/* Grades Selection with "All Grades" option */}
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Grades <span className="text-danger">*</span></Form.Label>
                    {loadingGrades && <Spinner animation="border" size="sm" className="ms-2" />}
                    {errors.grade_id && <div className="text-danger mb-2">{errors.grade_id}</div>}

                    {/* "All Grades" checkbox */}
                    <div className="grade-checkbox">
                      <Form.Check
                        type="checkbox"
                        id="grade-all"
                        label="All Grades"
                        checked={formData.grade_id.includes("all")}
                        onChange={(e) => handleGradeChange("all", e.target.checked)}
                        disabled={loading}
                      />
                    </div>
                    <hr />

                    {/* Individual grade checkboxes */}
                    <div className="row">
                      {grades.map((grade) => (
                        <div className="col-md-2" key={grade.id}>
                          <div className="grade-checkbox">
                            <Form.Check
                              type="checkbox"
                              id={`grade-${grade.id}`}
                              label={grade.name}
                              checked={formData.grade_id.includes("all") || formData.grade_id.includes(String(grade.id))}
                              onChange={(e) => handleGradeChange(String(grade.id), e.target.checked)}
                              disabled={loading || formData.grade_id.includes("all")}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              {/* Course Image Upload */}
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Course Image {!id && <span className="text-danger">*</span>}</Form.Label>
                    <Form.Control
                      type="file"
                      name="image"
                      accept="image/*"
                      onChange={handleChange}
                      disabled={loading}
                      isInvalid={!!errors.image}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.image}
                    </Form.Control.Feedback>

                    {/* Display existing or preview image */}
                    {(existingImage || imagePreview) && (
                      <div className="mt-2">
                        {existingImage && !imagePreview ? (
                          <div>
                            <img
                              src={existingImage}
                              alt="Current Course"
                              style={{
                                width: '150px',
                                height: '100px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '1px solid #ddd',
                                padding: '5px'
                              }}
                            />
                          </div>
                        ) : imagePreview && (
                          <div>
                            <img
                              src={imagePreview}
                              alt="Preview"
                              style={{
                                width: '150px',
                                height: '100px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '2px solid #28a745',
                                padding: '5px'
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {/* Course Description with Rich Text Editor */}
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Description <span className="text-danger">*</span></Form.Label>
                    <div className="ckeditor-wrapper" style={{ borderRadius: "4px" }}>
                      <CKEditor
                        editor={ClassicEditor}
                        data={formData.description}
                        style={{ borderRadius: "4px" }}
                        config={{
                          licenseKey: 'GPL',
                          height: '350px',
                          minHeight: '300px',
                        }}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          setFormData({ ...formData, description: data });
                          if (errors.description) {
                            setErrors({ ...errors, description: "" });
                          }
                        }}
                      />
                    </div>
                    {errors.description && (
                      <div className="invalid-feedback d-block">
                        {errors.description}
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {/* Form Action Buttons */}
              <div className="text-center mt-4">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={loading}
                  className="me-2"
                >
                  {loading ? "Processing..." : (id ? "Update" : "Add")}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => navigate("/admin/course")}
                  disabled={loading}
                >
                  Back
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>

      <ToastContainer />
    </>
  );
};

export default AddCourse;